const cancelledProviderAuthCodes = new Set([
  'auth/cancelled-popup-request',
  'auth/popup-closed-by-user'
])

const providerAuthError = (message, code = 'auth/provider-sign-in-failed') => {
  const error = new Error(message || 'Sign-in failed.')
  error.code = code
  return error
}

const isCancelledProviderAuth = error => Boolean(
  error && cancelledProviderAuthCodes.has(error.code)
)

export const authenticateWithProvider = async ({
  firebase,
  providerName,
  createProvider,
  linkUser,
  nativeSignIn,
  nativeComplete,
  nativeAbort,
  mergeAccount
}) => {
  let nativeTransaction = null
  let firebaseResult = null
  const abortNativeTransaction = async () => {
    if (!nativeTransaction || !nativeAbort) return null
    const transaction = nativeTransaction
    let response = null
    try {
      response = await nativeAbort(transaction)
    } catch (ignored) {
    }
    nativeTransaction = null
    return { transaction, response }
  }
  const completedAbortOutcome = outcome => Boolean(
    outcome?.response?.ok &&
    outcome.response.outcome === 'completed' &&
    outcome.response.uid === outcome.transaction.uid &&
    firebase.auth().currentUser?.uid === outcome.transaction.uid
  )

  try {
    if (nativeSignIn) {
      const response = await nativeSignIn({
        mode: linkUser ? 'link' : 'signIn',
        expectedUID: linkUser ? linkUser.uid : undefined
      })
      if (!response || response.cancelled) return null
      if (!response.ok || !response.uid || !response.transactionId) {
        return {
          error: providerAuthError(
            response.error,
            response.errorCode || 'auth/provider-sign-in-failed'
          )
        }
      }
      nativeTransaction = {
        transactionId: response.transactionId,
        uid: response.uid
      }
      if (linkUser && response.uid !== linkUser.uid) {
        await abortNativeTransaction()
        return {
          error: providerAuthError(
            `${providerName} sign-in did not preserve the current IntelliKey account.`,
            'auth/user-mismatch'
          )
        }
      }
      if (linkUser && firebase.auth().currentUser?.uid !== linkUser.uid) {
        await abortNativeTransaction()
        return {
          error: providerAuthError(
            `The signed-in IntelliKey account changed during ${providerName} sign-in.`,
            'auth/user-changed'
          )
        }
      }
      if (!nativeComplete) {
        await abortNativeTransaction()
        return {
          error: providerAuthError(
            `The app could not finish ${providerName} sign-in.`,
            'auth/native-completion-missing'
          )
        }
      }

      let result
      if (linkUser) {
        result = { user: linkUser }
      } else {
        if (!response.customToken) {
          await abortNativeTransaction()
          return {
            error: providerAuthError(
              `${providerName} sign-in did not return a Firebase credential.`,
              'auth/custom-token-missing'
            )
          }
        }
        result = await firebase.auth().signInWithCustomToken(response.customToken)
        if (!result?.user || result.user.uid !== response.uid) {
          await firebase.auth().signOut()
          await abortNativeTransaction()
          return {
            error: providerAuthError(
              `${providerName} sign-in returned a different IntelliKey account.`,
              'auth/user-mismatch'
            )
          }
        }
      }
      firebaseResult = result

      const completion = await nativeComplete(nativeTransaction)
      if (!completion?.ok || completion.uid !== response.uid) {
        const abortOutcome = await abortNativeTransaction()
        if (completedAbortOutcome(abortOutcome)) return firebaseResult
        if (!linkUser) await firebase.auth().signOut()
        return {
          error: providerAuthError(
            completion?.error || `The keyboard could not finish ${providerName} sign-in.`,
            completion?.errorCode || 'auth/native-completion-failed'
          )
        }
      }
      nativeTransaction = null
      return result
    }

    const provider = createProvider()
    if (linkUser) {
      if (mergeAccount && linkUser.providerData?.some(identity => identity.providerId === provider.providerId)) {
        return await verifyAndMergeProvider({ firebase, provider, linkUser, mergeAccount })
      }
      try { return await linkUser.linkWithPopup(provider) } catch (error) {
        if (!mergeAccount || !error.credential || !['auth/credential-already-in-use', 'auth/email-already-in-use'].includes(error.code)) throw error
        return await verifyAndMergeProvider({ firebase, provider, credential: error.credential, linkUser, mergeAccount })
      }
    }
    return await firebase.auth().signInWithPopup(provider)
  } catch (error) {
    if (nativeTransaction) {
      const abortOutcome = await abortNativeTransaction()
      if (completedAbortOutcome(abortOutcome)) return firebaseResult
      if (!linkUser) {
        try {
          await firebase.auth().signOut()
        } catch (ignored) {
        }
      }
    }
    if (isCancelledProviderAuth(error)) return null
    return { error }
  }
}
import { verifyAndMergeProvider } from './AccountMerge.js'

